// src/components/sections/HeroSection.js
import React, { useRef, useState, useEffect } from 'react';
import {
    Container, Row, Col, Button, Spinner, Image,
    Modal, Form, Alert, Card, Badge
} from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaPhoneAlt, FaCalendarAlt, FaStar, FaHeartbeat,
    FaPause, FaVolumeUp, FaVolumeMute, FaUserMd,
    FaCalendarDay, FaClock, FaUser, FaEnvelope,
    FaPhone, FaNotesMedical, FaCheckCircle,
    FaArrowLeft, FaArrowRight, FaCheck, FaInfoCircle,
    FaStethoscope, FaMapMarkerAlt
} from 'react-icons/fa';
import banner from '../../media/banner.mp4';
import logo from '../../media/logo.png';

const HeroSection = () => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [selectedDoctorAvailability, setSelectedDoctorAvailability] = useState(null);

    const [formData, setFormData] = useState({
        // Step 1 Data
        fullName: '',
        email: '',
        phone: '',
        age: '',
        gender: '',
        previousPatient: 'no',

        // Step 2 Data
        department: '',
        doctor: '',

        // Step 3 Data
        appointmentDate: '',
        appointmentTime: '',
        symptoms: '',
        preferredContact: 'email',
        insuranceProvider: '',
        emergencyContact: ''
    });

    // Departments data with descriptions
    const departments = [
        {
            id: 'gynecology',
            name: 'Gynecology & Obstetrics',
            icon: <FaUserMd />,
            description: 'Comprehensive women\'s health, pregnancy care, and childbirth services',
            color: '#FF7F50'
        },
        {
            id: 'pediatrics',
            name: 'Pediatrics',
            icon: <FaHeartbeat />,
            description: 'Healthcare for infants, children, and adolescents',
            color: '#20B2AA'
        },
        {
            id: 'fertility',
            name: 'Fertility & IVF',
            icon: <FaStar />,
            description: 'Advanced fertility treatments and reproductive assistance',
            color: '#9370DB'
        },
        {
            id: 'neonatal',
            name: 'Neonatal Care',
            icon: <FaCalendarDay />,
            description: 'Specialized care for newborn babies',
            color: '#87CEEB'
        },
        {
            id: 'surgery',
            name: 'Pediatric Surgery',
            icon: <FaNotesMedical />,
            description: 'Surgical care for children of all ages',
            color: '#3CB371'
        },
        {
            id: 'emergency',
            name: 'Emergency Care',
            icon: <FaPhoneAlt />,
            description: '24/7 emergency medical services',
            color: '#FF4500'
        }
    ];

    // Doctors data with availability status
    const doctorsByDepartment = {
        'gynecology': [
            {
                id: 'dr-sarah-johnson',
                name: 'Dr. Sarah Johnson',
                specialization: 'Senior Gynecologist',
                experience: '15 years',
                availability: 'Available Today',
                nextAvailable: 'Today, 2:30 PM',
                rating: 4.9,
                patients: '5000+',
                isAvailable: true,
                consultationFee: '$150'
            },
            {
                id: 'dr-priya-sharma',
                name: 'Dr. Priya Sharma',
                specialization: 'Obstetric Specialist',
                experience: '12 years',
                availability: 'Available Tomorrow',
                nextAvailable: 'Tomorrow, 10:00 AM',
                rating: 4.8,
                patients: '4200+',
                isAvailable: true,
                consultationFee: '$140'
            }
        ],
        'pediatrics': [
            {
                id: 'dr-michael-chen',
                name: 'Dr. Michael Chen',
                specialization: 'Pediatric Specialist',
                experience: '18 years',
                availability: 'Available Today',
                nextAvailable: 'Today, 4:00 PM',
                rating: 4.9,
                patients: '6000+',
                isAvailable: true,
                consultationFee: '$130'
            },
            {
                id: 'dr-robert-williams',
                name: 'Dr. Robert Williams',
                specialization: 'Child Healthcare',
                experience: '10 years',
                availability: 'Available Now',
                nextAvailable: 'Now',
                rating: 4.7,
                patients: '3500+',
                isAvailable: true,
                consultationFee: '$120'
            }
        ],
        'fertility': [
            {
                id: 'dr-emily-rodriguez',
                name: 'Dr. Emily Rodriguez',
                specialization: 'Fertility Specialist',
                experience: '14 years',
                availability: 'Available Tomorrow',
                nextAvailable: 'Tomorrow, 11:00 AM',
                rating: 4.9,
                patients: '2800+',
                isAvailable: true,
                consultationFee: '$200'
            }
        ],
        'neonatal': [
            {
                id: 'dr-james-wilson',
                name: 'Dr. James Wilson',
                specialization: 'Neonatal Specialist',
                experience: '20 years',
                availability: '24/7 Available',
                nextAvailable: 'Anytime',
                rating: 4.9,
                patients: '7000+',
                isAvailable: true,
                consultationFee: '$180'
            }
        ],
        'surgery': [
            {
                id: 'dr-alex-kumar',
                name: 'Dr. Alex Kumar',
                specialization: 'Pediatric Surgeon',
                experience: '16 years',
                availability: 'Available Monday',
                nextAvailable: 'Monday, 9:00 AM',
                rating: 4.8,
                patients: '3200+',
                isAvailable: false,
                consultationFee: '$250'
            }
        ],
        'emergency': [
            {
                id: 'emergency-team',
                name: 'Emergency Team',
                specialization: 'Emergency Medicine',
                experience: '24/7 Service',
                availability: '24/7 Available',
                nextAvailable: 'Immediate',
                rating: 4.9,
                patients: '15000+',
                isAvailable: true,
                consultationFee: 'Emergency Fee'
            }
        ]
    };

    const timeSlots = [
        '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
        '12:00 PM', '12:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
        '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM'
    ];

    const genderOptions = [
        { value: 'female', label: 'Female' },
        { value: 'male', label: 'Male' },
        { value: 'other', label: 'Other' },
        { value: 'prefer-not-to-say', label: 'Prefer not to say' }
    ];

    useEffect(() => {
        const videoElement = videoRef.current;

        if (videoElement) {
            console.log('Setting up video element');

            const handleCanPlay = () => {
                console.log('Video can play - showing video');
                setIsLoading(false);
            };

            const handleError = (e) => {
                console.error('Video error:', e);
                setIsLoading(false);
                setHasError(true);
            };

            const handleLoadedData = () => {
                console.log('Video loaded data');
                setIsLoading(false);
            };

            videoElement.addEventListener('canplay', handleCanPlay);
            videoElement.addEventListener('loadeddata', handleLoadedData);
            videoElement.addEventListener('error', handleError);

            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log('Auto-play prevented:', error);
                    setIsLoading(false);
                });
            }

            if (videoElement.readyState >= 3) {
                console.log('Video already loaded');
                setIsLoading(false);
            }

            return () => {
                videoElement.removeEventListener('canplay', handleCanPlay);
                videoElement.removeEventListener('loadeddata', handleLoadedData);
                videoElement.removeEventListener('error', handleError);
            };
        }
    }, []);

    useEffect(() => {
        // Reset selected doctor availability when department changes
        if (formData.department && !formData.doctor) {
            setSelectedDoctorAvailability(null);
        }

        // Update availability when doctor is selected
        if (formData.doctor && formData.department) {
            const doctors = doctorsByDepartment[formData.department];
            const selectedDoc = doctors.find(doc => doc.id === formData.doctor);
            if (selectedDoc) {
                setSelectedDoctorAvailability(selectedDoc);
            }
        }
    }, [formData.department, formData.doctor]);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch(e => {
                    console.log('Play failed:', e);
                });
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const nextStep = () => {
        // Validate current step before proceeding
        if (currentStep === 1) {
            if (!formData.fullName || !formData.email || !formData.phone || !formData.age || !formData.gender) {
                alert('Please fill all required fields in Step 1');
                return;
            }
        } else if (currentStep === 2) {
            if (!formData.department || !formData.doctor) {
                alert('Please select a department and doctor');
                return;
            }
        }
        setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmitAppointment = (e) => {
        e.preventDefault();
        console.log('Appointment form submitted:', formData);

        // Simulate API call
        setTimeout(() => {
            setFormSubmitted(true);
            setTimeout(() => {
                setShowAppointmentModal(false);
                setFormSubmitted(false);
                setCurrentStep(1);
                // Reset form
                setFormData({
                    fullName: '',
                    email: '',
                    phone: '',
                    age: '',
                    gender: '',
                    previousPatient: 'no',
                    department: '',
                    doctor: '',
                    appointmentDate: '',
                    appointmentTime: '',
                    symptoms: '',
                    preferredContact: 'email',
                    insuranceProvider: '',
                    emergencyContact: ''
                });
                setSelectedDoctorAvailability(null);
            }, 3000);
        }, 1500);
    };

    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    const getMaxDate = () => {
        const threeMonthsLater = new Date();
        threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
        return threeMonthsLater.toISOString().split('T')[0];
    };

    const renderStepIndicator = () => (
        <div className="step-indicator mb-4">
            <div className="d-flex justify-content-between position-relative">
                <div className="position-absolute top-50 start-0 end-0 h-1 bg-light"
                    style={{ zIndex: 1, opacity: 0.3 }}></div>
                {[1, 2, 3].map((step) => (
                    <div key={step} className="d-flex flex-column align-items-center position-relative"
                        style={{ zIndex: 2 }}>
                        <div className={`rounded-circle d-flex align-items-center justify-content-center ${currentStep >= step ? 'bg-primary' : 'bg-secondary'}`}
                            style={{ width: '40px', height: '40px', color: 'white' }}>
                            {currentStep > step ? <FaCheck /> : step}
                        </div>
                        <span className="mt-2 fw-medium" style={{ fontSize: '0.85rem' }}>
                            {step === 1 && 'Personal Info'}
                            {step === 2 && 'Select Doctor'}
                            {step === 3 && 'Appointment'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                    >
                        <h4 className="fw-bold mb-4" style={{ color: 'var(--dark-teal)' }}>
                            <FaUser className="me-2" />
                            Personal Information
                        </h4>

                        <div className="row g-3">
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Full Name *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaUser />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="fullName"
                                            placeholder="Enter your full name"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Email Address *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaEnvelope />
                                        </span>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            placeholder="Enter your email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Phone Number *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaPhone />
                                        </span>
                                        <Form.Control
                                            type="tel"
                                            name="phone"
                                            placeholder="Enter your phone number"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Age *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="age"
                                        min="0"
                                        max="120"
                                        placeholder="Enter your age"
                                        value={formData.age}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Gender *</Form.Label>
                                    <Form.Select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Gender</option>
                                        {genderOptions.map(gender => (
                                            <option key={gender.value} value={gender.value}>
                                                {gender.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Previous Patient at Our Hospital?</Form.Label>
                                    <div className="d-flex gap-4">
                                        <Form.Check
                                            type="radio"
                                            name="previousPatient"
                                            id="previous-yes"
                                            label="Yes"
                                            value="yes"
                                            checked={formData.previousPatient === 'yes'}
                                            onChange={handleInputChange}
                                        />
                                        <Form.Check
                                            type="radio"
                                            name="previousPatient"
                                            id="previous-no"
                                            label="No"
                                            value="no"
                                            checked={formData.previousPatient === 'no'}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                        </div>

                        <Alert variant="info" className="mt-4">
                            <FaInfoCircle className="me-2" />
                            Your information is secure and confidential. We'll use this to contact you about your appointment.
                        </Alert>
                    </motion.div>
                );

            case 2:
                return (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                    >
                        <h4 className="fw-bold mb-4" style={{ color: 'var(--dark-teal)' }}>
                            <FaUserMd className="me-2" />
                            Select Department & Doctor
                        </h4>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-medium">Select Department *</Form.Label>
                            <div className="row g-3">
                                {departments.map(dept => (
                                    <Col md={6} key={dept.id}>
                                        <Card
                                            className={`department-card ${formData.department === dept.id ? 'selected' : ''}`}
                                            onClick={() => setFormData({ ...formData, department: dept.id, doctor: '' })}
                                            style={{
                                                cursor: 'pointer',
                                                border: formData.department === dept.id ? `2px solid ${dept.color}` : '1px solid #dee2e6',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            <Card.Body className="py-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3" style={{ color: dept.color }}>
                                                        {dept.icon}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold">{dept.name}</h6>
                                                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                                            {dept.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </div>
                        </Form.Group>

                        {formData.department && (
                            <>
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-medium">Select Doctor *</Form.Label>
                                    <div className="row g-3">
                                        {doctorsByDepartment[formData.department]?.map(doctor => (
                                            <Col md={12} key={doctor.id}>
                                                <Card
                                                    className={`doctor-card ${formData.doctor === doctor.id ? 'selected' : ''}`}
                                                    onClick={() => setFormData({ ...formData, doctor: doctor.id })}
                                                    style={{
                                                        cursor: 'pointer',
                                                        border: formData.doctor === doctor.id ? '2px solid var(--primary-teal)' : '1px solid #dee2e6',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    <Card.Body>
                                                        <Row className="align-items-center">
                                                            <Col xs={3} md={2} className="text-center">
                                                                <div style={{
                                                                    width: '60px',
                                                                    height: '60px',
                                                                    borderRadius: '50%',
                                                                    background: 'linear-gradient(135deg, var(--primary-teal), var(--light-teal))',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    margin: '0 auto'
                                                                }}>
                                                                    <FaUserMd size={24} />
                                                                </div>
                                                            </Col>
                                                            <Col xs={9} md={6}>
                                                                <h6 className="fw-bold mb-1">{doctor.name}</h6>
                                                                <p className="text-muted mb-1" style={{ fontSize: '0.9rem' }}>
                                                                    {doctor.specialization}
                                                                </p>
                                                                <div className="d-flex align-items-center">
                                                                    <FaStar className="text-warning me-1" size={14} />
                                                                    <span className="fw-medium me-2">{doctor.rating}</span>
                                                                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                                                        ({doctor.patients} patients)
                                                                    </span>
                                                                </div>
                                                            </Col>
                                                            <Col xs={12} md={4} className="text-md-end mt-2 mt-md-0">
                                                                <Badge bg={doctor.isAvailable ? "success" : "secondary"} className="mb-2">
                                                                    {doctor.availability}
                                                                </Badge>
                                                                <div>
                                                                    <small className="text-muted d-block">Next available:</small>
                                                                    <strong>{doctor.nextAvailable}</strong>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </div>
                                </Form.Group>

                                {selectedDoctorAvailability && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="doctor-availability-info p-3 rounded"
                                        style={{
                                            backgroundColor: selectedDoctorAvailability.isAvailable ? '#e8f5e9' : '#fff3cd',
                                            border: `1px solid ${selectedDoctorAvailability.isAvailable ? '#c3e6cb' : '#ffeaa7'}`
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <FaInfoCircle className="me-3"
                                                style={{ color: selectedDoctorAvailability.isAvailable ? '#28a745' : '#ffc107' }}
                                            />
                                            <div>
                                                <h6 className="fw-bold mb-1">
                                                    {selectedDoctorAvailability.name} - Availability Status
                                                </h6>
                                                <p className="mb-1">
                                                    <strong>Status:</strong> {selectedDoctorAvailability.availability}
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Next Available Slot:</strong> {selectedDoctorAvailability.nextAvailable}
                                                </p>
                                                <p className="mb-0">
                                                    <strong>Experience:</strong> {selectedDoctorAvailability.experience} •
                                                    <strong> Fee:</strong> {selectedDoctorAvailability.consultationFee}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </>
                        )}

                        {!formData.department && (
                            <Alert variant="warning" className="mt-3">
                                <FaInfoCircle className="me-2" />
                                Please select a department to see available doctors.
                            </Alert>
                        )}
                    </motion.div>
                );

            case 3:
                return (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                    >
                        <h4 className="fw-bold mb-4" style={{ color: 'var(--dark-teal)' }}>
                            <FaCalendarDay className="me-2" />
                            Appointment Details
                        </h4>

                        <Alert variant="primary" className="mb-4">
                            <div className="d-flex align-items-center">
                                <FaStethoscope className="me-3" size={24} />
                                <div>
                                    <strong>Selected:</strong> {formData.doctor && doctorsByDepartment[formData.department]?.find(d => d.id === formData.doctor)?.name}
                                    <br />
                                    <small>Please choose your preferred date and time</small>
                                </div>
                            </div>
                        </Alert>

                        <div className="row g-3">
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Preferred Date *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaCalendarDay />
                                        </span>
                                        <Form.Control
                                            type="date"
                                            name="appointmentDate"
                                            min={getMinDate()}
                                            max={getMaxDate()}
                                            value={formData.appointmentDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <Form.Text className="text-muted">
                                        Appointments available from tomorrow to 3 months ahead
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Preferred Time *</Form.Label>
                                    <Form.Select
                                        name="appointmentTime"
                                        value={formData.appointmentTime}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Time Slot</option>
                                        {timeSlots.map(time => (
                                            <option key={time} value={time}>
                                                {time}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Symptoms / Reason for Visit</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="symptoms"
                                        rows={3}
                                        placeholder="Please describe your symptoms or reason for appointment in detail"
                                        value={formData.symptoms}
                                        onChange={handleInputChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Helps the doctor prepare for your visit
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Preferred Contact Method</Form.Label>
                                    <Form.Select
                                        name="preferredContact"
                                        value={formData.preferredContact}
                                        onChange={handleInputChange}
                                    >
                                        <option value="email">Email</option>
                                        <option value="phone">Phone Call</option>
                                        <option value="sms">SMS</option>
                                        <option value="whatsapp">WhatsApp</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Insurance Provider (Optional)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="insuranceProvider"
                                        placeholder="e.g., Blue Cross, Aetna"
                                        value={formData.insuranceProvider}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">Emergency Contact (Optional)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="emergencyContact"
                                        placeholder="Name and phone number"
                                        value={formData.emergencyContact}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </div>

                        <Alert variant="info" className="mt-4">
                            <FaNotesMedical className="me-2" />
                            <strong>Important:</strong> This booking will be confirmed within 24 hours. Please arrive 15 minutes before your scheduled time.
                        </Alert>
                    </motion.div>
                );

            default:
                return null;
        }
    };

    const renderNavigationButtons = () => {
        if (formSubmitted) return null;

        return (
            <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <Button
                    variant="outline-secondary"
                    onClick={currentStep === 1 ? () => setShowAppointmentModal(false) : prevStep}
                    className="d-flex align-items-center"
                >
                    <FaArrowLeft className="me-2" />
                    {currentStep === 1 ? 'Cancel' : 'Back'}
                </Button>

                <Button
                    onClick={currentStep === 3 ? handleSubmitAppointment : nextStep}
                    style={{
                        backgroundColor: 'var(--primary-teal)',
                        borderColor: 'var(--primary-teal)',
                        padding: '10px 30px',
                        fontWeight: '600'
                    }}
                    className="d-flex align-items-center"
                >
                    {currentStep === 3 ? (
                        <>
                            <FaCheckCircle className="me-2" />
                            Confirm Booking
                        </>
                    ) : (
                        <>
                            Next
                            <FaArrowRight className="ms-2" />
                        </>
                    )}
                </Button>
            </div>
        );
    };

    return (
        <section
            className="hero-section"
            style={{
                position: 'relative',
                overflow: 'hidden',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                paddingTop: '80px'
            }}
        >
            {/* Video Background */}
            <div
                className="position-absolute w-100 h-100"
                style={{
                    top: 0,
                    left: 0,
                    zIndex: 1
                }}
            >
                <video
                    ref={videoRef}
                    autoPlay
                    muted={isMuted}
                    loop
                    playsInline
                    preload="auto"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'translate(-50%, -50%)',
                        minWidth: '100%',
                        minHeight: '100%',
                        opacity: isLoading ? 0 : 1,
                        transition: 'opacity 0.5s ease-in-out'
                    }}
                >
                    <source src={banner} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>

                {/* Dark overlay for text readability */}
                <div
                    className="position-absolute w-100 h-100"
                    style={{
                        background: 'linear-gradient(90deg, rgba(10, 26, 42, 0.85) 0%, rgba(10, 26, 42, 0.7) 50%, rgba(10, 26, 42, 0.4) 100%)',
                        zIndex: 2
                    }}
                />

                {/* Loading overlay */}
                {isLoading && !hasError && (
                    <div
                        className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            background: 'linear-gradient(135deg, #0a1a2a 0%, #1a3a5f 100%)',
                            zIndex: 3
                        }}
                    >
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    color: '#20B2AA',
                                    borderWidth: '3px'
                                }}
                            />
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                                className="mt-3 text-white"
                                style={{ fontSize: '0.9rem', fontWeight: '500' }}
                            >
                                Loading Healthcare Experience...
                            </motion.p>
                        </div>
                    </div>
                )}

                {/* Error state */}
                {hasError && (
                    <div
                        className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            background: 'linear-gradient(135deg, #0a1a2a 0%, #1a3a5f 100%)',
                            zIndex: 3
                        }}
                    >
                        <div className="text-center text-white p-4">
                            <FaHeartbeat size={48} className="text-warning mb-3" />
                            <h4 className="fw-bold mb-2">Video Unavailable</h4>
                            <p className="mb-0 opacity-75">Using enhanced visual experience instead</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Video Controls */}
            {!isLoading && !hasError && (
                <div
                    className="position-absolute d-flex gap-2"
                    style={{
                        bottom: '20px',
                        right: '20px',
                        zIndex: 4
                    }}
                >
                    <motion.button
                        whileHover={{ scale: 1.1, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                        whileTap={{ scale: 0.9 }}
                        onClick={togglePlay}
                        className="btn btn-sm glass-effect d-flex align-items-center justify-content-center"
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            background: 'rgba(0, 0, 0, 0.3)',
                            color: 'white',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isPlaying ? <FaPause /> : <FaCalendarAlt />}
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.1, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                        whileTap={{ scale: 0.9 }}
                        onClick={toggleMute}
                        className="btn btn-sm glass-effect d-flex align-items-center justify-content-center"
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            background: 'rgba(0, 0, 0, 0.3)',
                            color: 'white',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                    </motion.button>
                </div>
            )}

            {/* Main Content */}
            <Container style={{ position: 'relative', zIndex: 3, paddingTop: '40px', paddingBottom: '40px' }}>
                <Row className="align-items-center">
                    <Col lg={7} md={12} className="order-1 order-lg-1">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        >
                            {/* Main Headline - Responsive */}
                            <motion.h1
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="fw-bold text-white mb-4"
                                style={{
                                    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                                    lineHeight: '1.2'
                                }}
                            >
                                Complete Care for
                                <span className="d-block mt-2" style={{
                                    background: 'linear-gradient(90deg, #20B2AA, #87CEEB)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    textShadow: '0 2px 10px rgba(32, 178, 170, 0.3)'
                                }}>
                                    Women, Mothers & Children
                                </span>
                            </motion.h1>

                            {/* Sub-headline - Responsive */}
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="mb-4 mb-lg-5"
                                style={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: 'clamp(1rem, 2vw, 1.4rem)',
                                    lineHeight: '1.6',
                                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
                                }}
                            >
                                From <span style={{ color: '#FF7F50', fontWeight: '700' }}>pregnancy to pediatrics</span>,
                                <span style={{ color: '#20B2AA', fontWeight: '700' }}> fertility to advanced care</span> —
                                compassionate healthcare where it matters most
                            </motion.p>

                            {/* Trust Badge - Responsive */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="d-flex align-items-center flex-wrap gap-3 mb-4 glass-effect px-3 py-2 rounded-pill"
                                style={{
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    width: 'fit-content',
                                    maxWidth: '100%'
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <FaHeartbeat className="me-2" style={{ color: '#20B2AA', fontSize: 'clamp(1rem, 2vw, 1.2rem)' }} />
                                    <span style={{
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)'
                                    }}>
                                        Trusted by 50,000+ Families Since 1998
                                    </span>
                                </div>
                                <div className="d-flex align-items-center ms-2 ps-2 border-start border-white border-opacity-25">
                                    <FaStar style={{ color: '#FFD700', fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)' }} />
                                    <span className="ms-1 text-white opacity-90" style={{
                                        fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)'
                                    }}>4.9/5</span>
                                </div>
                            </motion.div>

                            {/* CTA Buttons - Responsive */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                                className="d-flex flex-wrap gap-3 mb-4 mb-lg-5"
                            >
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="shimmer"
                                >
                                    <Button
                                        size="lg"
                                        className="d-flex align-items-center justify-content-center px-4 py-3 shadow-lg"
                                        style={{
                                            borderRadius: '12px',
                                            fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                            fontWeight: '600',
                                            background: 'linear-gradient(135deg, #20B2AA, #2CD5C4)',
                                            border: 'none',
                                            boxShadow: '0 10px 30px rgba(32, 178, 170, 0.4)',
                                            transition: 'all 0.3s ease',
                                            minWidth: 'fit-content',
                                            width: '100%'
                                        }}
                                        onClick={() => setShowAppointmentModal(true)}
                                    >
                                        <FaCalendarAlt className="me-2" size={20} />
                                        Book Appointment
                                    </Button>
                                </motion.div>

                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    style={{ flex: 1, minWidth: '250px' }}
                                >
                                    <Button
                                        size="lg"
                                        variant="outline-light"
                                        className="d-flex align-items-center justify-content-center px-4 py-3 glass-effect w-100"
                                        style={{
                                            borderRadius: '12px',
                                            fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                            fontWeight: '600',
                                            borderWidth: '2px',
                                            backdropFilter: 'blur(10px)',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <FaPhoneAlt className="me-2" size={20} />
                                        Emergency: (234) 567-8900
                                    </Button>
                                </motion.div>
                            </motion.div>

                            {/* Trust Indicators - Responsive */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.0 }}
                                className="mt-4 mt-lg-5 pt-4 pt-lg-5 border-top border-white border-opacity-25"
                            >
                                <Row className="g-3 g-md-4 text-center">
                                    <Col xs={6} md={3}>
                                        <motion.div
                                            className="mb-3 mb-md-0"
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            <div className="fw-bold mb-2 glow-text" style={{
                                                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                                                background: 'linear-gradient(90deg, #FF7F50, #FF9B73)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}>
                                                25+
                                            </div>
                                            <p className="mb-0 text-white opacity-90" style={{
                                                fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)'
                                            }}>Years of Excellence</p>
                                        </motion.div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                        <motion.div
                                            className="mb-3 mb-md-0"
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            <div className="fw-bold mb-2 glow-text" style={{
                                                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                                                background: 'linear-gradient(90deg, #20B2AA, #87CEEB)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}>
                                                24×7
                                            </div>
                                            <p className="mb-0 text-white opacity-90" style={{
                                                fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)'
                                            }}>Care Available</p>
                                        </motion.div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                        <motion.div
                                            className="mb-3 mb-md-0"
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            <div className="fw-bold mb-2 glow-text" style={{
                                                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                                                background: 'linear-gradient(90deg, #87CEEB, #A3E1FF)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}>
                                                50+
                                            </div>
                                            <p className="mb-0 text-white opacity-90" style={{
                                                fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)'
                                            }}>Specialist Doctors</p>
                                        </motion.div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                        <motion.div
                                            className="mb-3 mb-md-0"
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            <div className="fw-bold mb-2 glow-text" style={{
                                                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                                                background: 'linear-gradient(90deg, #FFD700, #FFE55C)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}>
                                                15K+
                                            </div>
                                            <p className="mb-0 text-white opacity-90" style={{
                                                fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)'
                                            }}>Successful Deliveries</p>
                                        </motion.div>
                                    </Col>
                                </Row>
                            </motion.div>

                            {/* Quick Links - Responsive */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="mt-4 pt-3"
                            >
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="text-white opacity-75" style={{
                                        fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)'
                                    }}>Quick access:</span>
                                    {['Emergency Care', 'Women\'s Health', 'Pediatrics', 'Fertility'].map((service, index) => (
                                        <motion.a
                                            key={index}
                                            href={`#${service.toLowerCase().replace(/\s+/g, '-')}`}
                                            whileHover={{ scale: 1.05, backgroundColor: 'rgba(32, 178, 170, 0.2)' }}
                                            className="text-decoration-none px-3 py-1 py-md-2 rounded-pill quick-link"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                color: 'rgba(255, 255, 255, 0.9)',
                                                fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)',
                                                fontWeight: '500',
                                                transition: 'all 0.3s ease',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {service}
                                        </motion.a>
                                    ))}
                                </div>
                            </motion.div>
                        </motion.div>
                    </Col>

                    {/* Right side content area */}
                    <Col lg={5} md={12} className="order-2 order-lg-2">
                        {/* Space intentionally left for video background visibility */}
                    </Col>
                </Row>
            </Container>

            {/* Multi-Step Appointment Booking Modal */}
            <Modal
                show={showAppointmentModal}
                onHide={() => {
                    setShowAppointmentModal(false);
                    setCurrentStep(1);
                    setFormSubmitted(false);
                    setSelectedDoctorAvailability(null);
                }}
                size="lg"
                centered
                backdrop="static"
                className="appointment-modal"
            >
                <Modal.Header closeButton style={{
                    backgroundColor: 'var(--primary-teal)',
                    color: 'white',
                    borderBottom: 'none'
                }}>
                    <Modal.Title className="fw-bold d-flex align-items-center">
                        <FaCalendarAlt className="me-3" />
                        {formSubmitted ? 'Booking Confirmed' : 'Book Appointment'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '2rem' }}>
                    {formSubmitted ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-5"
                        >
                            <FaCheckCircle size={80} className="text-success mb-4" />
                            <h4 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                                Appointment Booked Successfully!
                            </h4>

                            <div className="appointment-summary p-4 rounded mt-4 mb-4"
                                style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                                <h6 className="fw-bold mb-3">Appointment Summary</h6>
                                <Row>
                                    <Col md={6}>
                                        <p className="mb-2"><strong>Patient:</strong> {formData.fullName}</p>
                                        <p className="mb-2"><strong>Doctor:</strong> {
                                            formData.doctor && doctorsByDepartment[formData.department]?.find(d => d.id === formData.doctor)?.name
                                        }</p>
                                    </Col>
                                    <Col md={6}>
                                        <p className="mb-2"><strong>Date:</strong> {new Date(formData.appointmentDate).toLocaleDateString()}</p>
                                        <p className="mb-2"><strong>Time:</strong> {formData.appointmentTime}</p>
                                    </Col>
                                </Row>
                            </div>

                            <Alert variant="success">
                                <div className="d-flex align-items-center">
                                    <FaInfoCircle className="me-2" />
                                    <div>
                                        <strong>Confirmation:</strong> A confirmation email and SMS will be sent to you shortly.
                                        <br />
                                        <small>Please arrive 15 minutes before your scheduled time.</small>
                                    </div>
                                </div>
                            </Alert>

                            <Button
                                variant="outline-primary"
                                onClick={() => {
                                    setShowAppointmentModal(false);
                                    setFormSubmitted(false);
                                    setCurrentStep(1);
                                }}
                                className="mt-3"
                            >
                                Close
                            </Button>
                        </motion.div>
                    ) : (
                        <>
                            {renderStepIndicator()}
                            {renderStepContent()}
                            {renderNavigationButtons()}
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {/* Responsive CSS */}
            <style jsx="true">{`
                .hero-section {
                    padding-top: 80px;
                }
                
                /* Responsive text sizes */
                @media (max-width: 768px) {
                    .hero-section {
                        padding-top: 70px;
                        min-height: 90vh;
                    }
                    
                    h1 {
                        font-size: 2.5rem !important;
                    }
                    
                    .lead {
                        font-size: 1.1rem !important;
                    }
                    
                    .btn-lg {
                        font-size: 1rem !important;
                        padding: 0.75rem 1.5rem !important;
                    }
                    
                    .trust-badge {
                        padding: 0.5rem 1rem !important;
                        font-size: 0.9rem !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .hero-section {
                        padding-top: 65px;
                        min-height: 85vh;
                    }
                    
                    h1 {
                        font-size: 2rem !important;
                        line-height: 1.3 !important;
                    }
                    
                    .lead {
                        font-size: 1rem !important;
                        line-height: 1.5 !important;
                    }
                    
                    .btn-lg {
                        font-size: 0.9rem !important;
                        padding: 0.7rem 1.2rem !important;
                        width: 100% !important;
                    }
                    
                    .trust-badge {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                        padding: 0.75rem !important;
                        border-radius: 12px !important;
                    }
                    
                    .border-start {
                        border-left: none !important;
                        border-top: 1px solid rgba(255, 255, 255, 0.25) !important;
                        padding-left: 0 !important;
                        padding-top: 0.5rem !important;
                        margin-left: 0 !important;
                    }
                    
                    /* Stack CTA buttons on mobile */
                    .d-flex.flex-wrap.gap-3 {
                        flex-direction: column;
                        gap: 1rem !important;
                    }
                    
                    /* Adjust trust indicators */
                    .row.g-3.g-md-4 {
                        gap: 1rem !important;
                    }
                    
                    .row.g-3.g-md-4 > div {
                        margin-bottom: 1rem;
                    }
                    
                    /* Quick links wrap */
                    .d-flex.flex-wrap.align-items-center.gap-2 {
                        gap: 0.5rem !important;
                    }
                    
                    .quick-link {
                        font-size: 0.8rem !important;
                        padding: 0.4rem 0.8rem !important;
                    }
                }
                
                @media (max-width: 400px) {
                    h1 {
                        font-size: 1.8rem !important;
                    }
                    
                    .lead {
                        font-size: 0.95rem !important;
                    }
                    
                    .trust-badge span {
                        font-size: 0.8rem !important;
                    }
                    
                    .display-5 {
                        font-size: 2rem !important;
                    }
                }
                
                /* Modal responsiveness */
                @media (max-width: 768px) {
                    .appointment-modal .modal-dialog {
                        margin: 10px;
                    }
                    
                    .appointment-modal .modal-header {
                        padding: 1rem 1.5rem;
                    }
                    
                    .appointment-modal .modal-body {
                        padding: 1.5rem;
                    }
                    
                    .department-card {
                        margin-bottom: 0.5rem;
                    }
                    
                    .doctor-card {
                        margin-bottom: 0.5rem;
                    }
                    
                    .step-indicator {
                        margin-bottom: 2rem;
                    }
                }
                
                @media (max-width: 576px) {
                    .appointment-modal .modal-body {
                        padding: 1rem;
                    }
                    
                    .step-indicator .d-flex.justify-content-between {
                        gap: 1rem;
                    }
                    
                    .department-card .d-flex.align-items-center {
                        flex-direction: column;
                        text-align: center;
                    }
                    
                    .doctor-card .row.align-items-center {
                        flex-direction: column;
                        text-align: center;
                    }
                    
                    .doctor-card .text-md-end {
                        text-align: center !important;
                        margin-top: 1rem;
                    }
                }
                
                /* Ensure content fits on small screens */
                @media (max-width: 320px) {
                    .hero-section {
                        padding-top: 60px;
                    }
                    
                    h1 {
                        font-size: 1.6rem !important;
                    }
                    
                    .lead {
                        font-size: 0.9rem !important;
                    }
                    
                    .btn-lg {
                        font-size: 0.85rem !important;
                        padding: 0.6rem 1rem !important;
                    }
                    
                    .quick-link {
                        font-size: 0.75rem !important;
                        padding: 0.3rem 0.6rem !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default HeroSection;